<script lang="ts" setup>
/// <reference types="google.maps" />
import { composeCanonicalAddress } from '@foodlista/shared/restaurants/utils'
import { useGoogleMaps } from '../composables/useGoogleMaps'

const props = withDefaults(defineProps<{
  handleInput?: (event: Event) => void
  ui?: Record<string, unknown>
  showMap?: boolean
  requireCoordinates?: boolean
}>(), {
  showMap: true,
  requireCoordinates: true,
})

const isError = defineModel<boolean>('error')

const model = defineModel<{
  address: string | null
  city: string | null
  region: string | null
  country: string | null
  postalCode: string | null
  streetNumber: string | null
  route: string | null
  coordinates: { lat: number, lng: number } | null | undefined
  name: string | null | undefined
}>({ required: true })

const { t } = useI18n()
const runtimeConfig = useRuntimeConfig()

const DEFAULT_CENTER = { lat: 41.8719, lng: 12.5674 }
const AREA_ZOOM = 16
const COUNTRY_ZOOM = 5

const inputRef = ref<{ inputRef: HTMLInputElement | null } | null>(null)
const mapContainer = ref<HTMLElement | null>(null)
const autocomplete = ref<google.maps.places.SearchBox>()

let mapInstance: google.maps.Map | undefined
let marker: google.maps.marker.AdvancedMarkerElement | undefined
let geocoder: google.maps.Geocoder | undefined
let geocodeToken = 0
let resolvedAddress: string | null = model.value.coordinates ? model.value.address : null

const streetLine = ref<string>(
  [model.value.route, model.value.streetNumber].filter(Boolean).join(' '),
)

const google = useGoogleMaps()

const addressText = computed({
  get: () => model.value.address ?? '',
  set: (value) => {
    model.value.address = value
  },
})

const pinArea = computed(() =>
  [model.value.city, model.value.region, model.value.postalCode, model.value.country]
    .filter(Boolean)
    .join(', '),
)

function getAddressComponent(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
) {
  const component = components.find(component =>
    component.types.includes(type),
  )

  return component?.short_name || null
}

const ADDRESS_COMPONENT_TYPES = {
  city: 'locality',
  region: 'administrative_area_level_1',
  country: 'country',
  postalCode: 'postal_code',
  streetNumber: 'street_number',
  route: 'route',
} as const

type AddressComponentKey = keyof typeof ADDRESS_COMPONENT_TYPES

const LOCKED_AREA_KEYS = ['city', 'region', 'postalCode', 'country'] as const

const ALL_ADDRESS_KEYS = Object.keys(ADDRESS_COMPONENT_TYPES) as AddressComponentKey[]

function applyAddressComponents(
  components: google.maps.GeocoderAddressComponent[],
  keys: readonly AddressComponentKey[],
) {
  for (const key of keys) {
    const value = getAddressComponent(components, ADDRESS_COMPONENT_TYPES[key])
    if (value) {
      model.value[key] = value
    }
  }
}

function ensureGeocoder() {
  if (!geocoder && google.value) {
    geocoder = new google.value.maps.Geocoder()
  }
  return geocoder
}

function setCoordinates(coordinates: { lat: number, lng: number }) {
  model.value.coordinates = coordinates

  if (marker) {
    marker.position = coordinates
  }
  if (mapInstance) {
    mapInstance.panTo(coordinates)
    mapInstance.setZoom(AREA_ZOOM)
  }
}

function readMarkerPosition() {
  const position = marker?.position
  if (!position) {
    return null
  }

  const lat = typeof position.lat === 'function' ? position.lat() : position.lat
  const lng = typeof position.lng === 'function' ? position.lng() : position.lng

  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null
  }

  return { lat, lng }
}

async function refreshAreaFromPin(location: google.maps.LatLngLiteral) {
  const instance = ensureGeocoder()
  if (!instance) {
    return
  }

  const token = ++geocodeToken

  try {
    const { results } = await instance.geocode({ location })

    if (token !== geocodeToken) {
      return
    }

    const components = results[0]?.address_components
    if (components) {
      applyAddressComponents(components, LOCKED_AREA_KEYS)

      if (streetLine.value.trim()) {
        model.value.address = composeCanonicalAddress(streetLine.value, model.value)
        resolvedAddress = model.value.address
      }
    }
  } catch {}
}

async function resolveTypedAddress(address: string) {
  streetLine.value = address

  const instance = ensureGeocoder()
  if (!instance) {
    return
  }

  const token = ++geocodeToken

  try {
    const { results } = await instance.geocode({ address })

    if (token !== geocodeToken) {
      return
    }

    const result = results[0]
    const location = result?.geometry.location

    if (!result || !location) {
      return
    }

    applyAddressComponents(result.address_components, LOCKED_AREA_KEYS)
    setCoordinates({ lat: location.lat(), lng: location.lng() })

    model.value.address = composeCanonicalAddress(streetLine.value, model.value)
    resolvedAddress = model.value.address
  } catch {}
}

watchEffect(() => {
  const input = inputRef.value?.inputRef
  if (!input || !google.value || autocomplete.value || !google.value.maps.places) {
    return
  }

  autocomplete.value = new google.value.maps.places.SearchBox(input)

  autocomplete.value.addListener('places_changed', () => {
    if (!autocomplete.value) {
      return
    }

    const places = autocomplete.value.getPlaces()
    const place = places?.[0]

    if (!place || !place.address_components) {
      return
    }

    const address = place.formatted_address || place.name || getAddressComponent(place.address_components, 'route') || ''

    model.value.address = address

    if (place.name && place.business_status && !model.value.name) {
      model.value.name = place.name
    }

    applyAddressComponents(place.address_components, ALL_ADDRESS_KEYS)

    streetLine.value = [model.value.route, model.value.streetNumber].filter(Boolean).join(' ')

    const lat = place.geometry?.location?.lat()
    const lng = place.geometry?.location?.lng()

    if (typeof lat === 'number' && typeof lng === 'number') {
      setCoordinates({ lat, lng })
    }

    resolvedAddress = address
  })
})

watchEffect(() => {
  if (!google.value || !google.value.maps.marker || !mapContainer.value || mapInstance) {
    return
  }

  const coordinates = model.value.coordinates

  mapInstance = new google.value.maps.Map(mapContainer.value, {
    center: coordinates ?? DEFAULT_CENTER,
    zoom: coordinates ? AREA_ZOOM : COUNTRY_ZOOM,
    mapId: runtimeConfig.public.googleMapId,
    disableDefaultUI: true,
    zoomControl: true,
    clickableIcons: false,
    gestureHandling: 'greedy',
  })

  marker = new google.value.maps.marker.AdvancedMarkerElement({
    map: mapInstance,
    position: coordinates ?? DEFAULT_CENTER,
    gmpDraggable: true,
  })

  marker.addListener('dragend', () => {
    const coordinates = readMarkerPosition()
    if (!coordinates) {
      return
    }

    model.value.coordinates = coordinates
    resolvedAddress = model.value.address
    refreshAreaFromPin(coordinates)
  })
})

function teardownMap() {
  if (marker) {
    google.value?.maps.event.clearInstanceListeners(marker)
    marker.map = null
    marker = undefined
  }
  if (mapInstance) {
    google.value?.maps.event.clearInstanceListeners(mapInstance)
    mapInstance = undefined
  }
}

async function blur() {
  const address = model.value.address?.trim() ?? ''

  if (!address) {
    isError.value = true
    return
  }

  if (address === resolvedAddress && model.value.coordinates) {
    isError.value = false
    return
  }

  await resolveTypedAddress(address)

  if (props.requireCoordinates && !props.showMap) {
    isError.value = !model.value.coordinates
  }
}

function onEnter() {
  setTimeout(() => {
    void blur()
  }, 0)
}

watch(() => model.value.address, () => {
  isError.value = false

  if (model.value.address !== resolvedAddress) {
    model.value.coordinates = null
  }
})

function handleInput(e: Event) {
  isError.value = false
  props.handleInput?.(e)
}

onUnmounted(teardownMap)
</script>

<template>
  <div class="flex flex-col gap-2">
    <UInput
      id="autocomplete"
      ref="inputRef"
      v-model="addressText"
      :placeholder="t('dashboard.index.createRestaurantModal.fields.address.placeholder')"
      icon="i-tabler-map-pin" :color="isError ? 'error' : undefined" :ui="props.ui"
      @input="handleInput"
      @keydown.enter.prevent="onEnter"
      @blur="blur"
    />

    <div v-if="props.showMap" class="flex flex-col gap-2">
      <p class="text-xs text-gray-500 dark:text-gray-400">
        {{ t('common.addressInput.mapHint') }}
      </p>
      <div class="h-56 w-full overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div ref="mapContainer" class="size-full" />
      </div>
      <p v-if="pinArea" class="text-xs text-gray-500 dark:text-gray-400">
        <span class="font-medium">{{ t('common.addressInput.pinArea') }}:</span>
        {{ pinArea }}
      </p>
    </div>
  </div>
</template>
